import { isEmpty } from 'lodash'
import { destroy, stopSubmit } from 'redux-form'
import { call, delay, put, select, takeLatest } from 'redux-saga/effects'
import calendarIsReady from '../../actions/bookingCalendar/calendar/calendarIsReady'
import { fetchCalendars } from '../../actions/bookingCalendar/calendar/fetchCalendar'
import { databaseForEventsIsEmpty } from '../../actions/bookingCalendar/event/requestDataForEvents'
import loadNotificationPage from '../../actions/notification/loadMore'
import { synchronizationCompleted } from '../../actions/synchronize/synchronize'
import { fireToastWarning } from '../../actions/toasts/fireToast'
import fireToastError from '../../actions/toasts/fireToastError'
import fireToastSuccess from '../../actions/toasts/fireToastSucces'
import {
  authorizationGranted,
  authorizationRejected,
  IAuthorizationGranted,
  IRequestAuthorize,
} from '../../actions/userAccount/authorize/authorize'
import { flushState } from '../../actions/userAccount/authorize/unauthorize'
import { requestUser } from '../../actions/userAccount/user/fetchUser'
import { languageChanged } from '../../actions/userAccount/user/switchLanguage'
import { localStorage as LocalStorage } from '../../database'
import { driver, names } from '../../database/localStorage'
import i18n from '../../i18n'
import { CountryCode } from '../../i18n/countryCodes'
import fetchFactory from '../../models/fetch'
import authRequest, { IAuthorizationPass } from '../../models/User/authorize'
import { CalendarActions } from '../../reducers/bookingCalendar/actionNames'
import getNotificationServiceStore from '../../reducers/notificationService/getNotificationServiceStore'
import { AuthorizationActions, UserAccountActions } from '../../reducers/userAccount/actionNames'
import takeLatestWithSignOff from '../../utils/Saga/TakeLatestUntilSignoff'
import { Mixpanel } from '../../utils/Mixpanel'
import { Hotjar } from '../../utils/HotJar/hotjar'
import { getUserId, getWorkerId } from '../../reducers/userAccount/selectors/userAccount'
import db from '../../database/indexedDb'
import store from '../../store'

function* rememberCredentials(action: IAuthorizationGranted) {
  const { Driver, Names } = LocalStorage
  const { CredentialsStorage } = Names

  if (isEmpty(action.accessToken)) {
    return yield put(fireToastWarning('z1125_cannot_remember_credentials'))
  }

  Driver.setItem(CredentialsStorage.AccessToken, action.accessToken)
  Driver.setItem(CredentialsStorage.UserID, action.username)
}

function* authorize(
  action: IRequestAuthorize & { accessToken: string; provider: string },
) {
  const { Driver, Names } = LocalStorage
  const { CredentialsStorage } = Names

  try {
    let workerId = null
    let accessToken =
      action.accessToken || Driver.getItem(CredentialsStorage.AccessToken, '')

    const isEmptyData =
      isEmpty(accessToken) &&
      (isEmpty(action.username) || isEmpty(action.password))

    if (isEmptyData) {
      const from = (window as any).routerHistory.location.pathname


      yield put(authorizationRejected())
      if ('routerHistory' in window) {
        ;(window as any).routerHistory.push('/login', {
          from: from && from.includes('login') ? '/' : from,
        })
      }
      return ''
    }

    if ('' === accessToken) {
      const fetchModel = authRequest({
        fetch: fetchFactory('').fetch,
      })

      yield put({ type: AuthorizationActions.FlushState })

      const authData: IAuthorizationPass = yield call(
        fetchModel,
        action.username,
        action.password,
      )
      accessToken = authData.access_token
      workerId = authData.id_worker
    }

    Hotjar.identify(action.username)

    localStorage.setItem(
      'provider',
      action.provider ? action.provider : 'E-mail',
    )

    yield put(
      authorizationGranted(accessToken, action.username, !action.accessToken),
    )

    if (workerId) {
      yield put({
        type: UserAccountActions.SetUserWorkerId,
        payload: workerId,
      })
    }
  } catch (err) {
    if ('routerHistory' in window) {
      ;(window as any).routerHistory.push('/login', {})
    }

    Driver.removeItem(CredentialsStorage.AccessToken)

    yield put(stopSubmit('loginForm', { password: ' ', username: ' ' }))
    if (err.message === 'NO PREMIUM') {
      yield put(fireToastError(i18n.t('BED_3621_account_blocked')))
    } else {
      yield put(fireToastError(i18n.t('errStatus_2')))
    }

    Mixpanel.track('Login Failed', {
      'Login Failed Method': 'E-mail',
      'Login Failed Email': action.username,
      'Login Failed Password Length': Number(action.password.length),
      'Login Failed Internet Active?': true,
      'Login Method': action.provider ? action.provider : 'E-mail',
    })
  }
}

function* successAuthorization(action: IAuthorizationGranted) {
  const notificationService = yield select(getNotificationServiceStore)

  if (notificationService.accessToken) {
    yield* runNotificationService()
  } else {
    yield put(destroy('loginForm'))

    const lang = driver.getItem(names.UserAccountStorage.SelectedLanguage, 'en')
    yield put(languageChanged(lang as CountryCode, false))

    yield delay(2000)
    const access = driver.getItem(names.CredentialsStorage.AccessToken, '')
    if (access && action.manual) {
      yield put(fireToastSuccess(i18n.t('user_logged_id')))
    }
  }
  yield put(loadNotificationPage(0))
}

function* runNotificationService() {
  const notificationService = yield select(getNotificationServiceStore)

  yield put(languageChanged(notificationService.lang as CountryCode))
  yield put(fetchCalendars())
  yield put(
    synchronizationCompleted(
      new Date(Number(notificationService.modifyDate) * 1000),
    ),
  )
  yield takeLatest(CalendarActions.CalendarsFetched, function*() {
    yield put(requestUser())
    yield put(databaseForEventsIsEmpty())
    yield put(calendarIsReady())
  })
  yield takeLatest(UserAccountActions.UserDataAccepted, function*() {
    yield put(databaseForEventsIsEmpty())
  })
  yield delay(500)
}

function* signOffUser(action) {
  const { Driver, Names } = LocalStorage
  const { CredentialsStorage } = Names

  try {
    yield db.events.clear()
    yield db.rooms.clear()
  } catch (e) {
    console.error(e)
  } finally {
    const userId = yield select(getUserId)
    let workerId = yield select(getWorkerId)
    if (workerId === undefined) {
      workerId = window.localStorage.getItem('workerId')
    }

    if (workerId && Number(workerId)) {
      Mixpanel.people.increment(userId, 'Subaccount Logout Used', 1)
    } else {
      Mixpanel.people.increment(userId, 'Logout Used', 1)
    }
    Mixpanel.track('Logout', {})
    Mixpanel.reset()

    Driver.removeItem(CredentialsStorage.AccessToken)
    Driver.removeItem(CredentialsStorage.UserID)

    yield put(flushState())

    if ('routerHistory' in window) {
      ;(window as any).routerHistory.push('/login', {})
    }

    if (action.fireToast) {
      store.dispatch(
        fireToastSuccess(
          i18n.t('z5032_alert_force_logout_title'),
          false,
          undefined,
          'force_logout',
        ),
      )
    } else {
      setTimeout(() => {
        store.dispatch(
          fireToastError(
            i18n.t('BED_3621_data_changed_or_account_delete'),
            false,
            undefined,
            'force_logout_info_deleted',
          ),
        )
      }, 1000)
    }

    window.localStorage.removeItem('workerId')
  }
}

function* socialtryLogin(action) {
  try {
    const fetch = fetchFactory('').fetch

    const hostname = window && window.location && window.location.hostname
    let mixpanelId = undefined

    if (
      hostname === 'app.bed-booking.com' ||
      hostname === 'react.bed-booking.com' ||
      hostname === 'pwa.bed-booking.com'
    ) {
      mixpanelId = '6e11ee3778b7bff044cf57983211f389'
    } else {
      mixpanelId = '8efe7a069d331b3b3dac68b8c66675bc'
    }

    const cookie = JSON.parse(
      decodeURIComponent(getCookie('mp_' + mixpanelId + '_mixpanel')),
    )

    const resp = yield fetch('/api/v2/user/social', {
      method: 'POST',
      body: JSON.stringify({
        token: action.payload.token,
        provider: action.payload.provider,
        from_website: 1,
        mixpanel_id: cookie.distinct_id,
        utm_source: action.payload.utm_source,
        utm_medium: action.payload.utm_medium,
        utm_content: action.payload.utm_content,
        utm_term: action.payload.utm_term,
        utm_campaign: action.payload.utm_campaign,
        initial_referrer: action.payload.initial_referrer,
        initial_referring_domain: action.payload.initial_referring_domain,
      }),
    })

    const data = yield resp.json()
    const { token: access_token, email: username } = data.items[0]

    const auth = (window as any).firebaseAuth.getAuth()
    const user = auth.currentUser
    ;(window as any).firebaseAuth.deleteUser(user)

    yield* authorize({
      accessToken: access_token,
      username,
      provider: action.payload.provider,
    } as any)

    // const resp = yield Effects.call(service, action.payload.token)
  } catch (e) {
    console.error(e)
    window.location.href = '/login'
    yield put(fireToastError(i18n.t('error_server_response_2')))
  }
}

function getCookie(name)
{
  const regex = new RegExp(`(^| )${name}=([^;]+)`)
  const match = document.cookie.match(regex)
  if (match) {
    return match[2]
  }
}

export default function*() {
  yield takeLatestWithSignOff(
    AuthorizationActions.RequestAuthorization,
    authorize,
  )

  yield takeLatest('Set notification service state', successAuthorization)

  yield takeLatestWithSignOff(
    AuthorizationActions.AuthorizationGranted,
    rememberCredentials,
  )
  yield takeLatestWithSignOff(
    AuthorizationActions.AuthorizationGranted,
    successAuthorization,
  )

  yield takeLatest(AuthorizationActions.TrySocialLogin, socialtryLogin)
  yield takeLatest(AuthorizationActions.SignOffTheAccount, signOffUser)
}
